import {
  LoadingOutlined,
  PictureOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { Popover, Spin, Tag } from "antd";
import { useEffect, useRef, useState } from "react";
import { BiSolidBellOff } from "react-icons/bi";
import { BsReplyFill } from "react-icons/bs";
import { MdFilePresent } from "react-icons/md";
import { RxDrawingPinFilled } from "react-icons/rx";
import searchImg from "../../assets/search.png";
import AvatarComponent from "../../components/avatar/AvatarComponent";
import { DropdownConversationItem } from "../../components/customComponent/DropdownConversationItem";
import {
  conversationTab,
  conversationType,
  isAlarmConversationType,
  MESSAGE_STATUS,
  REACTION_TYPE_LABEL,
} from "../../config/Constant";
import { StickerIcon } from "../../config/Icon";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { formatShortTime, formatTime } from "../../utils/formatTime";
import {
  extractContent,
  extractText,
  highlightText,
  truncateText,
} from "../../utils/Utils";
import "./style.scss";
import { LuAlarmClock } from "react-icons/lu";
import { socketStatus$ } from "../../api/webSocketRx";
import { getDisplayName } from "../../utils/ConversationHelper";

const ReactionLine = ({ conversation, isSelected }) => {
  const { getMember } = useAuthLayoutContext();

  const reactor = getMember(conversation?.reactionPreview?.reactorId);

  if (!conversation?.reactionPreview || isSelected) return null;

  return (
    <div className="reaction-preview text-[12px] mt-1">
      <span className="flex items-center mr-1 truncate">
        <span className="shrink-0">
          {
            REACTION_TYPE_LABEL.find(
              (r) => r.type === conversation.reactionPreview.reactionType,
            )?.label
          }
        </span>
        <span className="ml-1 text-[12px] text-gray-600 truncate">
          {reactor ? reactor?.name : conversation?.reactionPreview?.reactorName}
        </span>
      </span>
    </div>
  );
};

const AllChatBox = ({
  conversation,
  isSelected,
  onClick,
  languageMap,
  user,
  labelList,
  handleUpdateConversationList,
  onChangeLabel,
  onChangeStar,
  labelSelectedId,
  conversationList,
  orgList,
}) => {
  const getSenderName = () => {
    const isCurrentUser = conversation?.lastSenderId === user?.userId;

    if (isCurrentUser && conversation?.type !== conversationType.NOTIFICATION) {
      return (
        <span className="mr-[3px]">
          {languageMap?.["sender.you"] ?? "You:"}
        </span>
      );
    }

    if (conversation?.type === conversationType.GROUP) {
      const senderName = getDisplayName(
        orgList?.find((org) => org?.userCode === conversation?.lastSenderId)
          ?.name,
      );

      return senderName && <span className=" mr-[3px]">{senderName}: </span>;
    }

    return null;
  };

  const showConversationTime = () => {
    return conversation?.lastMessageAt
      ? formatShortTime(
          new Date(conversation?.lastMessageAt),
          languageMap,
          user?.language,
        )
      : "";
  };

  const getLastMessage = () => {
    if (conversation?.lastMessageType === MESSAGE_STATUS.STICKER) {
      return (
        <div className="flex items-center gap-1">
          <StickerIcon width={20} height={20} className="text-[18px]" />
          <span>Sticker</span>
        </div>
      );
    }

    if (conversation?.type === "NOTIFICATION") {
      const regexText = conversation?.lastMessage
        ?.replace(/<\/?[^>]+(>|$)/g, " ")
        ?.split(", ")
        ?.filter((name) => name !== user?.name)
        ?.join(", ");
      return truncateText(regexText, 30);
    }

    if (conversation?.type === "DRAFT") {
      const { text, images } = extractContent(conversation?.lastMessage || "");

      if (text?.trim() === "" && images?.length > 0) {
        return languageMap?.["images"] ?? "images";
      }

      if (text?.trim() === "") {
        return (
          languageMap?.["conversation.lastMessage.draftMessage"] ??
          "Draft message"
        );
      }

      return truncateText(text, 26);
    }

    if (conversation?.type === conversationType.SCHEDULE) {
      return `Scheduled at ${formatTime(conversation?.scheduleAt)}`;
    }

    if (conversation?.lastMessage === "conversation.lastMessage.removed") {
      return truncateText(
        languageMap?.[conversation?.lastMessage] ??
          "The message has been removed",
        30,
      );
    }

    if (conversation?.lastMessageType === MESSAGE_STATUS.IMAGE) {
      return (
        <>
          <PictureOutlined className="mr-1 text-[#666666]" />
          <span>
            {languageMap?.["modal.conversation.sendPhoto"] ?? "Photo"}
          </span>
        </>
      );
    }

    if (
      ["PROMOTED_MESSAGE", "PINNED_MESSAGE", "UNPINNED_MESSAGE"].includes(
        conversation?.lastMessageType,
      )
    ) {
      return (
        <>
          {languageMap?.[conversation?.lastMessageType] ??
            conversation?.lastMessageType}
        </>
      );
    }

    if (conversation?.lastMessageType === MESSAGE_STATUS.DISABLE_GROUP) {
      return languageMap?.["DISABLE_GROUP"] ?? "";
    }

    if (conversation?.lastMessageType === MESSAGE_STATUS.ENABLE_GROUP) {
      return languageMap?.["ENABLE_GROUP"] ?? "";
    }

    if (conversation?.lastMessageType === MESSAGE_STATUS.REMOVE_MEMBER) {
      return (
        <>
          {conversation?.lastMessage}{" "}
          {languageMap?.["messageList.conversation.kickOut"] ??
            "was kicked out of group"}
        </>
      );
    }

    return truncateText(conversation?.lastMessage, 30);
  };

  const getLabelName = (labelId) => {
    if (labelId) {
      return labelList?.find((s) => s.labelId === labelId)?.labelName;
    }
  };

  const unreadCountContent = () => {
    if (
      !conversation?.unreadCount ||
      conversation?.unreadCount === 0 ||
      conversation?.conversationStatus === "INACTIVE"
    )
      return;

    return (
      <span className="flex justify-center items-center absolute p-1 bg-red-500 rounded-full text-white text-[10px] top-[36px] w-[50px] h-[16px] z-[15]">
        {conversation?.unreadCount > 99 ? "99+" : conversation?.unreadCount}
      </span>
    );
  };

  const summaryConversationName = (conversation) => {
    if (conversation?.type === conversationType.DRAFT && !conversation?.name)
      return (
        <span className="text-[16px] font-medium">
          {languageMap?.["conversation.lastMessage.draftMessage"] ??
            "Draft message"}
        </span>
      );

    if (
      conversation?.type === conversationType.NOTIFICATION &&
      ["mail.noTitleMail", "", null].includes(conversation?.name)
    ) {
      return (
        <span
          className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
        >
          {languageMap?.["mail.noTitleMail"]}
        </span>
      );
    }

    return (
      <span
        className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
      >
        {truncateText(conversation?.name, 30)}
      </span>
    );
  };

  return (
    <div
      key={conversation?.conversationId}
      className={`conversation conversation-item flex gap-2 ${isSelected ? "selectedConversation" : ""}`}
      onClick={() => {
        onClick(conversation);
      }}
    >
      <div className="relative flex self-center shrink-0">
        <AvatarComponent conversation={conversation} />
        {unreadCountContent()}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="conversation-name flex justify-between items-center gap-2">
          <div className="name truncate flex-1 font-semibold">
            {summaryConversationName(conversation)}
          </div>

          <div className="time-and-bell text-[12px] conversation-item flex items-center shrink-0 gap-1">
            {isAlarmConversationType(conversation) && !conversation?.alarm && (
              <BiSolidBellOff
                size={16}
                className="icon-conversation-item visible"
                color="#5e6c84"
              />
            )}

            {isAlarmConversationType(conversation) &&
              conversation?.pinnedConversationTs && (
                <RxDrawingPinFilled
                  size={16}
                  className="icon-conversation-item visible"
                  color="#5e6c84"
                />
              )}

            {isAlarmConversationType(conversation) ||
            conversation?.type === conversationType.NOTIFICATION ? (
              <>
                <span
                  className="action-button"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <DropdownConversationItem
                    conversation={conversation}
                    languageMap={languageMap}
                    conversationList={conversationList}
                    handleUpdateConversationList={handleUpdateConversationList}
                    selectedConversation={isSelected ? conversation : null}
                    labelList={labelList}
                    onChangeLabel={onChangeLabel}
                    labelSelectedId={labelSelectedId}
                  />
                </span>

                <span
                  className={`conversation-time ${conversation?.unreadCount !== 0 ? "font-semibold" : ""}`}
                >
                  {showConversationTime(conversation)}
                </span>
              </>
            ) : (
              <span
                className={
                  conversation?.unreadCount !== 0 ? "font-semibold" : ""
                }
              >
                {showConversationTime(conversation)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between w-full overflow-hidden gap-2 mt-1">
          <div className="last-message flex items-center flex-1 min-w-0 text-[12px] font-medium text-gray-500">
            <span className="shrink-0 whitespace-nowrap">
              {getSenderName(conversation)}
            </span>

            <span
              className={`truncate flex-1 ${
                conversation?.unreadCount !== 0 ? "font-semibold" : ""
              }`}
            >
              {getLastMessage() ||
                (languageMap?.["messageList.conversation.new"] ??
                  "Welcome to Chat")}
            </span>
          </div>

          <div className="flex items-center shrink-0 gap-1">
            <div className="flex items-center">
              {conversation?.scheduleCount > 0 && (
                <LuAlarmClock size={20} color={"#3269c9"} />
              )}
              {conversation?.type === conversationType.NOTIFICATION &&
                conversation?.hasFile && (
                  <MdFilePresent size={20} color={"#3269c9"} />
                )}
              {conversation?.type === conversationType.NOTIFICATION &&
                conversation?.hasReply && (
                  <BsReplyFill size={20} color={"#3269c9"} />
                )}
            </div>

            {conversation?.labelId && getLabelName(conversation?.labelId) && (
              <Popover content={getLabelName(conversation?.labelId)}>
                <Tag color="#87d068" className="name-tag mr-0">
                  {getLabelName(conversation?.labelId)}
                </Tag>
              </Popover>
            )}

            <button
              className="star-item flex items-center"
              onClick={(event) => {
                event.stopPropagation();
                event.nativeEvent.stopImmediatePropagation();
                onChangeStar(conversation?.userConversationId);
              }}
            >
              <p className="star m-0">
                <StarFilled
                  className="star-filled"
                  style={{ display: conversation?.starred ? "block" : "none" }}
                />
                <StarOutlined
                  className="text-[#979899]"
                  style={{ display: conversation?.starred ? "none" : "block" }}
                />
              </p>
            </button>
          </div>
        </div>
        <ReactionLine conversation={conversation} isSelected={isSelected} />
      </div>
    </div>
  );
};

const SearchChatBox = ({
  conversation,
  isSelected,
  onClick,
  languageMap,
  user,
  conversationSearch,
}) => {
  const normalizeText = (text) => text.replace(/\s+/g, " ").trim();
  const getDisplayMessageWithKeyword = (text, keyword, maxLength = 50) => {
    if (!text || typeof text !== "string") return "";

    text = normalizeText(text);

    if (!keyword || typeof keyword !== "string") {
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    }

    const lowerText = text.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    const index = lowerText.indexOf(lowerKeyword);

    if (index === -1) {
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    }

    const start = Math.max(
      0,
      index - Math.floor((maxLength - keyword.length) / 2),
    );
    const end = start + maxLength;
    const sliced = text.slice(start, end);

    return (start > 0 ? "..." : "") + sliced + (end < text.length ? "..." : "");
  };

  const highlightText = (text, keyword) => {
    if (!keyword) return [text];
    const regex = new RegExp(
      `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    return text.split(regex).map((part, idx) => (
      <span
        key={idx}
        style={{
          fontWeight:
            part.toLowerCase() === keyword.toLowerCase() ? "bold" : "normal",
          color:
            part.toLowerCase() === keyword.toLowerCase() ? "red" : "inherit",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {part}
      </span>
    ));
  };

  const isCurrentUser = conversation?.senderId === user?.userId;
  const sender = isCurrentUser
    ? languageMap?.["modal.conversation.senderName"] ?? "You"
    : conversation?.senderName;
  const time = conversation?.createdAt
    ? formatShortTime(
        new Date(conversation.createdAt),
        languageMap,
        user?.language,
      )
    : "";

  const contentMap = {
    TEXT: conversation?.content,
    IMAGE: conversation?.content,
    FILE: conversation?.shortName,
  };

  const messageText = contentMap[conversation?.contentType] || "";

  const keyword = conversationSearch?.textSearch || "";
  const displayMessage = getDisplayMessageWithKeyword(messageText, keyword);

  return (
    <div
      className={`search-conversation ${isSelected ? "selectedConversation" : ""}`}
      onClick={() => {
        onClick(conversation);
      }}
    >
      <div className="relative flex self-center">
        <AvatarComponent conversation={conversation} />
      </div>
      <div className="w-[100%]">
        <div className="conversation-name">
          <div className="name">
            <b className="text-[15px]">
              {conversation?.type !== "DRAFT"
                ? languageMap?.[conversation?.name] ??
                  truncateText(conversation?.name, 36)
                : languageMap?.["conversation.lastMessage.draftMessage"] ??
                  "Draft message"}
            </b>
          </div>
          <div className="time-and-bell text-[12px]">{time}</div>
        </div>
        <div className="conversation-content-message">
          <div className="last-message highlight-search">
            <span className="text-[#2a56b9] mr-[3px] max-w-[153px]">
              {getDisplayName(sender) ? `${getDisplayName(sender)}:` : ""}
            </span>
            <div className="flex-1 italic text-gray-500">
              {highlightText(displayMessage, keyword)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SearchMessageBox = ({ conversation, orgList }) => {
  const { user, languageMap } = useInfoUser();
  const { conversationSearch, onChangeConversation, selectedMailId } =
    useMenuContext();

  const highlightText = (text, keyword) => {
    if (!keyword) return [text];
    const regex = new RegExp(
      `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    return text.split(regex).map((part, idx) => (
      <span
        key={idx}
        style={{
          fontWeight:
            part.toLowerCase() === keyword.toLowerCase() ? "bold" : "normal",
          color:
            part.toLowerCase() === keyword.toLowerCase() ? "red" : "inherit",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {part}
      </span>
    ));
  };

  const isCurrentUser = conversation?.senderId === user?.userId;
  const sender = isCurrentUser
    ? languageMap?.["modal.conversation.senderName"] ?? "You"
    : conversation?.senderName ||
      orgList?.find((org) => org?.userCode === conversation?.senderId)?.name;
  const time = conversation?.createdAt
    ? formatShortTime(
        new Date(conversation.createdAt),
        languageMap,
        user?.language,
      )
    : "";

  const messageText = conversation?.plainText;

  const keyword = conversationSearch?.textSearch || "";
  const displayMessage = extractText(messageText, keyword);

  const summaryConversationName = (conversation) => {
    if (
      conversation?.type === conversationType.NOTIFICATION &&
      ["mail.noTitleMail", "", null].includes(conversation?.conversationName)
    ) {
      return (
        <span
          className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
        >
          {languageMap?.["mail.noTitleMail"]}
        </span>
      );
    }

    return (
      <span
        className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
      >
        {truncateText(conversation?.conversationName, 30)}
      </span>
    );
  };

  return (
    <div
      className={`search-conversation ${selectedMailId === conversation?.mailId ? "selectedConversation" : ""}`}
      onClick={() => onChangeConversation(conversation)}
    >
      <div className="relative flex self-center">
        <AvatarComponent conversation={conversation} />
      </div>
      <div className="w-[100%]">
        <div className="conversation-name">
          <div className="name">{summaryConversationName(conversation)}</div>
          <div className="time-and-bell text-[12px]">{time}</div>
        </div>
        <div className="conversation-content-message">
          <div className="last-message flex flex-wrap text-[12px] max-w-[100%]">
            <span className="text-[#2a56b9] mr-[3px]">
              {getDisplayName(sender) ? `${getDisplayName(sender)}:` : ""}
            </span>
            <div className="flex-1 italic text-gray-500">
              {highlightText(displayMessage, conversationSearch?.textSearch)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConversationName = ({ conversation, keyword }) => {
  const { languageMap } = useInfoUser();

  if (["mail.noTitleMail", "", null].includes(conversation?.conversationName)) {
    return (
      <span
        className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
      >
        {languageMap?.["mail.noTitleMail"]}
      </span>
    );
  }

  return (
    <span
      className={`text-[16px] ${conversation?.unreadCount ? "font-bold" : "font-medium"}`}
    >
      {highlightText(
        extractText(conversation?.conversationName, keyword, 30),
        keyword,
      )}
    </span>
  );
};

const SearchMessageBoxV2 = ({ conversation, orgList }) => {
  const { user, languageMap } = useInfoUser();
  const { conversationSearch, onChangeConversation, selectedMailId } =
    useMenuContext();

  const sender =
    conversation?.senderId === user?.userId
      ? languageMap?.["modal.conversation.senderName"] ?? "You"
      : conversation?.senderName ||
        orgList?.find((org) => org?.userCode === conversation?.senderId)?.name;

  const time = formatShortTime(
    new Date(conversation?.createdAt),
    languageMap,
    user?.language,
  );

  const keyword = conversationSearch?.textSearch || "";
  const displayMessage = extractText(conversation?.plainText, keyword);

  return (
    <div
      className={`search-conversation ${selectedMailId === conversation?.letterId && "selectedConversation"}`}
      onClick={() => onChangeConversation(conversation)}
    >
      <div className="relative flex self-center">
        <AvatarComponent conversation={conversation} />
      </div>
      <div className="w-[100%]">
        <div className="conversation-name">
          <ConversationName conversation={conversation} keyword={keyword} />
          <div className="time-and-bell text-[12px]">{time}</div>
        </div>
        {conversation?.senderId && (
          <div className="conversation-content-message">
            <div className="last-message flex flex-wrap text-[12px] max-w-[100%]">
              <span className="text-[#2a56b9] mr-[3px]">
                {getDisplayName(sender)}
              </span>
              <div className="flex-1 italic text-gray-500">
                {highlightText(displayMessage, keyword)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationList = () => {
  const {
    selectedConversation,
    conversationList,
    onChangeConversation,
    handleUpdateConversationList,
    onChangeLabel,
    onChangeStar,
    labelSelectedId,
    conversationRef,
    isMoreConversation,
    isConversationsLoading,
    fetchMoreConversations,
    fetchMoreMailConversations,
    fetchMoreLetterContact,
    fetchMoreSearchChat,
    keydownMenuOn,
    resetAndReloadConversations,
    activeKey,
    conversationSearch,
    selectedMessageId,
    conversationHeight,
    jumpToSearchChat,
    labelList,
  } = useMenuContext();

  const { orgList } = useAuthLayoutContext();

  const { user, languageMap } = useInfoUser();
  const lastObserver = useRef();
  const [countReconnectedSuccessWs, setCountReconnectedSuccessWs] = useState(1);

  const lastConversationRef = (node) => {
    if (isConversationsLoading || !isMoreConversation) return;
    if (lastObserver.current) {
      lastObserver.current.disconnect();
    }

    lastObserver.current = new IntersectionObserver(async ([entry]) => {
      if (!entry?.isIntersecting) return;

      if (conversationTab.SEARCH_CHAT === activeKey) {
        await fetchMoreSearchChat();
        return;
      }

      if ([conversationTab.CONTACT_MESSAGE].includes(activeKey)) {
        await fetchMoreLetterContact(activeKey);
        return;
      }

      if (
        [
          conversationTab.NEW_SEARCH_MESSAGE_RECEIVE,
          conversationTab.NEW_SEARCH_MESSAGE_SEND,
        ].includes(activeKey)
      ) {
        await fetchMoreMailConversations(activeKey);
        return;
      }

      if (
        [
          conversationTab.SEARCH_MESSAGE_RECEIVE,
          conversationTab.SEARCH_MESSAGE_SEND,
        ].includes(activeKey)
      ) {
        // do something for search mail
        await fetchMoreMailConversations(activeKey);
      } else {
        await fetchMoreConversations();
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const scrollToSelectedConversation = () => {
    if (!selectedConversation) return;

    const container = conversationRef?.current;
    if (!container) return;

    const selectedEl = container.querySelector(".selectedConversation");
    if (!selectedEl) return;

    try {
      const containerRect = container.getBoundingClientRect();
      const elRect = selectedEl.getBoundingClientRect();
      const offset =
        elRect.top -
        containerRect.top -
        container.clientHeight / 2 +
        elRect.height / 2;
      container.scrollTop += offset;
    } catch (err) {
      console.log("manual scroll error:", err);
    }
  };

  const generateConversation = (conversation, index) => {
    switch (activeKey) {
      case conversationTab.ALL:
      case conversationTab.UNREAD:
      case conversationTab.CHAT:
      case conversationTab.NOTICE:
      case conversationTab.MESSAGE_RECEIVE:
      case conversationTab.MESSAGE_SEND:
      case conversationTab.CONTACT_CHAT:
      case conversationTab.CONTACT_MESSAGE:
      case conversationTab.SCHEDULE:
      case conversationTab.SEARCH_NOTICE:
        // if (!conversation?.name && !getLastMessage(conversation)) return null;

        return (
          <div
            key={conversation?.conversationId}
            ref={
              index === conversationList?.length - 1
                ? lastConversationRef
                : null
            }
          >
            <AllChatBox
              conversation={conversation}
              isSelected={
                selectedConversation?.conversationId ===
                conversation?.conversationId
              }
              onClick={onChangeConversation}
              languageMap={languageMap}
              user={user}
              labelList={labelList}
              handleUpdateConversationList={handleUpdateConversationList}
              onChangeLabel={onChangeLabel}
              onChangeStar={onChangeStar}
              labelSelectedId={labelSelectedId}
              conversationList={conversationList}
              orgList={orgList}
            />
          </div>
        );

      // case conversationTab.CONTACT_MESSAGE:
      //   // if (!conversation?.name && !getLastMessage(conversation)) return null;
      //
      //   return (
      //     <div
      //       key={conversation?.conversationId}
      //       ref={
      //         index === conversationList?.length - 1
      //           ? lastConversationRef
      //           : null
      //       }
      //     >
      //       <AllChatBox
      //         conversation={conversation}
      //         isSelected={
      //           selectedConversation?.conversationId ===
      //           conversation?.conversationId
      //         }
      //         onClick={onChangeConversation}
      //         languageMap={languageMap}
      //         user={user}
      //         labelList={labelList}
      //         handleUpdateConversationList={handleUpdateConversationList}
      //         onChangeLabel={onChangeLabel}
      //         onChangeStar={onChangeStar}
      //         labelSelectedId={labelSelectedId}
      //         conversationList={conversationList}
      //         orgList={orgList}
      //       />
      //     </div>
      //   );
      case conversationTab.SEARCH_CHAT:
        return (
          <div
            key={conversation?.messageId}
            ref={
              index === conversationList?.length - 1
                ? lastConversationRef
                : null
            }
          >
            <SearchChatBox
              conversation={conversation}
              isSelected={selectedMessageId === conversation?.messageId}
              onClick={jumpToSearchChat}
              languageMap={languageMap}
              user={user}
              conversationSearch={conversationSearch}
            />
          </div>
        );
      /** rework later 2 below cases */
      case conversationTab.SEARCH_MESSAGE_RECEIVE:
      case conversationTab.SEARCH_MESSAGE_SEND:
        return (
          <div
            key={conversation?.mailId}
            ref={
              index === conversationList.length - 1 ? lastConversationRef : null
            }
          >
            <SearchMessageBox conversation={conversation} orgList={orgList} />
          </div>
        );
      case conversationTab.NEW_SEARCH_MESSAGE_RECEIVE:
      case conversationTab.NEW_SEARCH_MESSAGE_SEND:
        conversationTab.NEW_SEARCH_MESSAGE_SEND === activeKey
          ? (conversation.role = "HOST")
          : (conversation.role = "MEMBER");

        return (
          <div
            key={conversation?.letterId}
            ref={
              index === conversationList.length - 1 ? lastConversationRef : null
            }
          >
            <SearchMessageBoxV2 conversation={conversation} orgList={orgList} />
          </div>
        );
      default:
        console.log("other active key");
    }
  };

  useEffect(() => {
    const sub = socketStatus$.subscribe((eventData) => {
      if (!eventData) return;

      if (
        eventData?.status === "CONNECTED" &&
        countReconnectedSuccessWs < eventData?.connectedSuccess
      ) {
        setCountReconnectedSuccessWs(eventData?.connectedSuccess);
        resetAndReloadConversations();
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId, user?.userId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!keydownMenuOn) return;
      if (!conversationList?.length) return;

      const currentIndex = conversationList?.findIndex(
        (c) => c.conversationId === selectedConversation?.conversationId,
      );

      if (e.key === "ArrowDown") {
        const nextIndex = (currentIndex + 1) % conversationList?.length;
        onChangeConversation(conversationList[nextIndex]);
        scrollToSelectedConversation();
      } else if (e.key === "ArrowUp") {
        const prevIndex = Math.max(0, currentIndex - 1);
        onChangeConversation(conversationList[prevIndex]);
        scrollToSelectedConversation();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    conversationList,
    selectedConversation,
    onChangeConversation,
    keydownMenuOn,
  ]);

  useEffect(() => {
    return () => {
      if (lastObserver.current) lastObserver.current.disconnect();
    };
  }, [activeKey]);

  if (
    !isConversationsLoading &&
    conversationTab.SEARCH_CHAT === activeKey &&
    (!conversationSearch?.textSearch || conversationSearch?.textSearch === "")
  ) {
    return (
      <div className="flex flex-col items-center">
        <img src={searchImg} alt="Cover" className="w-[40%]" />
        <div className="px-[44px] text-center text-[#5a6981]">
          Enter keywords to start searching for messages and files in the
          conversation
        </div>
      </div>
    );
  }

  if (!isConversationsLoading && conversationList?.length === 0) {
    return (
      <div className="flex flex-col items-center">
        <img src={searchImg} alt="Cover" className="w-[40%]" />
        <div className="px-[44px] text-center text-[#5a6981]">
          No results found
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list relative">
      <div
        ref={conversationRef}
        className="conversation-container"
        style={{
          maxHeight: `${conversationHeight}px`,
        }}
      >
        {conversationList?.map((conversation, index) => {
          return generateConversation(conversation, index);
        })}
      </div>
      {isConversationsLoading && (
        <div className="absolute top-[30%] left-[50%]">
          <Spin indicator={<LoadingOutlined className="text-[30px]" spin />} />
        </div>
      )}
    </div>
  );
};

export { ConversationList };
